import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { observer } from "mobx-react";
import styled from "styled-components";
import { pfidGetTokenUrl, pfidClientID, pfidClientSecret, pfidCreateAccountUrl, pfidConnectionUrl, pfidRedirectUri } from "../../../_services/api/user/settings";
import { generateCodeChallenge, generateCodeVerifier, getCodeVerifier, setCodeVerifier } from "../../../_services/api/user";
import { Button, Image } from "../../_ui";
import labels from '../../../_translations/inscription.json';
import store from "../../../_services/store";
import { setStorageAuthToken } from "../../../_services/utils/token";
import { ROUTES } from "../../../router/routes";
import { API_URL_PRD } from "../../../_services/api/config/config";
import { STORAGE_KEYS } from "../../../constants";
import { ReactComponent as FrFlag } from '../../../assets/svg/flags/fr.svg';
import { ReactComponent as EnFlag } from '../../../assets/svg/flags/en.svg';
import { ReactComponent as EsFlag } from '../../../assets/svg/flags/es.svg';
import { ReactComponent as ItFlag } from '../../../assets/svg/flags/it.svg';
import { FlagsContainer, FlagContainer } from "./components";

const flags: TFlag[] = [
  {
    key: 'fr',
    element: <FrFlag />
  },
  {
    key: 'en',
    element: <EnFlag />
  },
  {
    key: 'es',
    element: <EsFlag />
  },
  {
    key: 'it',
    element: <ItFlag />
  }
];

const ExplanationContainer = styled.div`
  background: #A75550;
  border-radius: 20px;
  padding: 20px;
  margin: 20px 20px;
  color: white;
  max-width: 500px;
  text-align: left;
  width: calc(100% - 40px);
  box-sizing: border-box;

  @media (min-width: 768px) {
    margin: 20px 0;
    width: 500px;
  }
`;

const ExplanationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const ExplanationTitle = styled.h3`
  color: white;
  font-size: 18px;
  font-weight: bold;
  margin: 0;
`;

const ToggleButton = styled.button`
  background: white;
  color: #A75550;
  border: none;
  border-radius: 15px;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f0f0f0;
  }
`;

const ExplanationContent = styled.div<{ $isExpanded: boolean }>`
  max-height: ${props => props.$isExpanded ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const ScenarioBox = styled.div`
  background: white;
  color: #A75550;
  border-radius: 15px;
  padding: 15px;
  margin: 10px 0;
  font-weight: bold;
`;

const InstructionText = styled.div`
  color: white;
  font-size: 14px;
  margin: 10px 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PlusIcon = styled.div`
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #A75550;
  font-weight: bold;
  font-size: 16px;
  flex-shrink: 0;
`;

const StepContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 8px 0;
`;

const StepNumber = styled.div`
  width: 25px;
  height: 25px;
  background: white;
  color: #A75550;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  flex-shrink: 0;
`;

const StepText = styled.div`
  color: white;
  font-size: 13px;
  line-height: 1.4;
`;

const Auth = observer(({ authorize = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [codeChallenge, setCodeChallenge] = useState<string | null>(null);
  const [isExplanationExpanded, setIsExplanationExpanded] = useState<boolean>(false);
  const [loginError, setLoginError] = useState<boolean>(false);

  const code = new URLSearchParams(location.search).get('code');
  const [generatedState, setGeneratedState] = useState<string>('');
  // Auto-détection : si on a un code dans l'URL, on est dans le flow d'autorisation
  const isAuthorizationCallback = !!code;
  //const redirectUri = new URLSearchParams(location.search).get('state') || '';

  const selectLanguage = (lang: Lang) => {
    store.setCurrentLanguage(lang);
  }

  const toggleExplanation = () => {
    setIsExplanationExpanded(!isExplanationExpanded);
  }

  useEffect(() => {
    if (authorize || isAuthorizationCallback) {
      const codeVerifier = getCodeVerifier();
      const receivedState = new URLSearchParams(location.search).get('state');
      console.log('Auth component:', { codeVerifier }, { code }, { receivedState }, { authorize }, { isAuthorizationCallback });
      console.log('PFID Config:', { pfidClientID, pfidClientSecret: pfidClientSecret?.substring(0, 10) + '...', pfidGetTokenUrl, pfidRedirectUri });

      if (code && codeVerifier) {
        console.log(`${pfidGetTokenUrl}?grant_type=authorization_code&code=${code}&redirect_uri=${encodeURIComponent(pfidRedirectUri)}&client_id=${pfidClientID}&code_verifier=${codeVerifier}`);

        const tokenData = new URLSearchParams({
          grant_type: 'authorization_code',
          code: code,
          redirect_uri: pfidRedirectUri,
          code_verifier: codeVerifier
        });

        // Créer l'authentification Basic avec client_id:client_secret
        const basicAuth = btoa(`${pfidClientID}:${pfidClientSecret}`);
        
        console.log('Token request details:', {
          url: pfidGetTokenUrl,
          clientId: pfidClientID,
          clientSecret: pfidClientSecret?.substring(0, 10) + '...',
          basicAuth: basicAuth?.substring(0, 20) + '...',
          body: tokenData.toString()
        });

        fetch(pfidGetTokenUrl, {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Authorization': `Basic ${basicAuth}`,
          },
          body: tokenData
        }
        )
          .then((response) => {
            console.log('Token response status:', response.status);
            return response.json();
          })
          .then((response) => {
            console.log('Token response:', response);
            const token = response.access_token
            
            if (token) {
              // Le token PFID n'est PAS un token d'API. Le poser ici faisait
              // basculer le routeur sur les pages connectees, qui lancaient
              // aussitot des appels proteges avec ce token : le backend les
              // refuse (401), le client vide la session et recharge la page.
              // L'utilisateur se retrouvait sur l'ecran de connexion et devait
              // cliquer une seconde fois. On attend le token applicatif renvoye
              // par /user/login.
              const data = new URLSearchParams({ token: token });

              fetch(`${API_URL_PRD}/api/public/user/login`, {
                method: 'POST',
                body: data,
                //@ts-ignore
                headers: {
                  "Content-Type": "application/x-www-form-urlencoded",
                  Accept: "application/json",
                  "X-PF-Allowed-Proxy": 1,
                },
              })
              .then(async res => {
                console.log('Login response status:', res.status);
                const body = await res.json().catch(() => null);
                return { ok: res.ok, status: res.status, body };
              })
              .then(({ ok, status, body }) => {
                console.log('Login response:', body);

                // Le backend repond par un corps JSON contenant le seul code
                // (ex: 401) en cas d'echec : il faut donc verifier le statut HTTP
                // ET la presence du token, sinon on enchaine sur une session vide.
                const authToken =
                  ok && body && typeof body === 'object' ? body.auth_token : undefined;

                if (!authToken) {
                  console.error('Login failed:', status, body);
                  setLoginError(true);
                  return;
                }

                setStorageAuthToken(authToken);
                store.setAuthToken(authToken);
                store.storeProfile({ key: STORAGE_KEYS.user, data: body });

                if (document.domain === 'localhost') {
                  document.cookie = `jwt_pfid=${encodeURI(response.access_token)};path=/;`;
                } else {
                  document.cookie = `jwt_pfid=${encodeURI(response.access_token)};domain=.${document.domain};path=/;`;
                }

                navigate(ROUTES.HOME());
              })
              .catch(err => {
                console.error("LOGIN ERR", err);
              })
            }
          }).catch(error => {
            console.error('PFID Token exchange error:', error);
          });
      }
    }
  }, [code, isAuthorizationCallback]);

  useEffect(() => {
    if (!authorize && !isAuthorizationCallback) {
      const codeVerifier = generateCodeVerifier();
      const state = generateCodeVerifier(); // Utiliser la même fonction pour générer un state aléatoire
      setCodeVerifier(codeVerifier);
      setGeneratedState(state);
      generateCodeChallenge(codeVerifier).then((codeGenerated) => setCodeChallenge(codeGenerated));
    }
  }, []);

  const flagItems = flags.map(flag => (
    <FlagContainer key={flag.key} onClick={() => selectLanguage(flag.key)}>
      {flag.element}
    </FlagContainer>
  ));

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      textAlign: 'center', 
      gap: '10px', 
      marginTop: '5%'
    }}>
      <Image
        src="https://pfid.pierre-fabre.dev/logo.svg"
        alt="Pierre Fabre ID Logo"
        width='256px'
        height='auto'
      />

      {/* Texte d'explication pliable */}
      <ExplanationContainer>
        <ExplanationHeader>
          <ExplanationTitle>{labels[store.currentLanguage].explanation.title}</ExplanationTitle>
          <ToggleButton onClick={toggleExplanation}>
            {isExplanationExpanded 
              ? labels[store.currentLanguage].explanation.showLess 
              : labels[store.currentLanguage].explanation.showMore
            }
          </ToggleButton>
        </ExplanationHeader>
        
        <ExplanationContent $isExpanded={isExplanationExpanded}>
          <ScenarioBox>
            {labels[store.currentLanguage].explanation.existingUser.title}
          </ScenarioBox>
          <InstructionText>
            <PlusIcon>+</PlusIcon>
            {labels[store.currentLanguage].explanation.existingUser.instruction}
          </InstructionText>

          <ScenarioBox>
            {labels[store.currentLanguage].explanation.newUser.title}
          </ScenarioBox>
          
          <StepContainer>
            <StepNumber>1</StepNumber>
            <StepText>{labels[store.currentLanguage].explanation.newUser.steps["1"]}</StepText>
          </StepContainer>
          
          <StepContainer>
            <StepNumber>2</StepNumber>
            <StepText>{labels[store.currentLanguage].explanation.newUser.steps["2"]}</StepText>
          </StepContainer>
          
          <StepContainer>
            <StepNumber>3</StepNumber>
            <StepText>{labels[store.currentLanguage].explanation.newUser.steps["3"]}</StepText>
          </StepContainer>
          
          <StepContainer>
            <StepNumber>4</StepNumber>
            <StepText>{labels[store.currentLanguage].explanation.newUser.steps["4"]}</StepText>
          </StepContainer>
        </ExplanationContent>
      </ExplanationContainer>

      <Button
        title={labels[store.currentLanguage].title}
        callback={() => window.location.assign(pfidCreateAccountUrl)}
      />

      {loginError && (
        <div style={{
          color: '#A75550',
          fontWeight: 'bold',
          maxWidth: 500,
          padding: '0 20px',
        }}>
          {labels[store.currentLanguage].network.loginFailed}
        </div>
      )}

      <Button
        title={labels[store.currentLanguage].currentaccount}
        callback={() => {
          if (codeChallenge) {
            // Vérifier si l'URL contient déjà des paramètres
            const hasParams = pfidConnectionUrl.includes('?');
            const separator = hasParams ? '&' : '?';
            
            // Construire seulement les paramètres manquants (PKCE + theme + state) - pas de scope
            const additionalParams = `code_challenge=${codeChallenge}&code_challenge_method=S256&theme=sos&state=${encodeURIComponent(generatedState)}`;
            
            const finalUrl = `${pfidConnectionUrl}${separator}${additionalParams}`;
            console.log('Redirecting to PFID:', finalUrl);
            window.location.assign(finalUrl);
          }
        }}
      />

      {/* Sélecteur de langue à 100px sous le bouton */}
      <div style={{ marginTop: '100px' }}>
        <FlagsContainer>
          {flagItems}
        </FlagsContainer>
      </div>

      {/* Logo Avène sous le sélecteur de langue */}
      <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'center' }}>
        <img
          src="/images/logo_avene2.png"
          alt="Avène"
          style={{ maxWidth: 280, maxHeight: 100, width: 'auto', height: 'auto', objectFit: 'contain' }}
        />
      </div>

    </div>
  );
});

export default Auth;
